import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cotAxios } from "./api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Copy, ExternalLink, Check, X, FileSpreadsheet, Package, Users, Trophy } from "lucide-react";

const copyToClipboard = async (txt) => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(txt);
    } else {
      const ta = document.createElement("textarea");
      ta.value = txt; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    toast.success("Link copiado!");
  } catch { toast.error("Falha ao copiar"); }
};

export default function CotacaoDetalhePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cotacao, setCotacao] = useState(null);
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      cotAxios.get(`/cotacao/cotacoes/${id}`).then((r) => r.data),
      cotAxios.get(`/cotacao/cotacoes/${id}/links`).then((r) => r.data),
    ]).then(([c, l]) => {
      // Reescreve loja_url + whatsapp_url usando o próprio origin do browser.
      // Isso torna o link imune ao hostname interno do cluster que o backend
      // eventualmente devolve quando não recebe X-Forwarded-Host / Origin.
      if (l?.fornecedores) {
        const publicOrigin = window.location.origin;
        const urlRegex = /https?:\/\/[^\s]*\/cotacoes\/responder\/[a-f0-9-]+/i;
        l.fornecedores = l.fornecedores.map((f) => {
          const lojaUrl = `${publicOrigin}/cotacoes/responder/${f.token_publico}`;
          const novaMsg = (f.mensagem || "").replace(urlRegex, lojaUrl);
          const raw = (f.celular || "").replace(/\D/g, "");
          const num = raw && (raw.length === 10 || raw.length === 11) ? "55" + raw : raw;
          const waUrl = `https://wa.me/${num}?text=${encodeURIComponent(novaMsg)}`;
          return { ...f, loja_url: lojaUrl, whatsapp_url: waUrl, mensagem: novaMsg };
        });
      }
      setCotacao(c); setLinks(l);
    })
      .catch(() => toast.error("Erro ao carregar cotação"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-10 text-slate-500">Carregando...</div>;
  if (!cotacao) return <div className="text-center py-10 text-slate-500">Cotação não encontrada.</div>;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="cot-detalhe-page">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => nav("/cotacoes/dashboard/cotacoes")} data-testid="btn-voltar-detalhe">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-1">
        <FileSpreadsheet className="w-7 h-7 text-blue-700" />
        <h1 className="text-2xl font-bold">Cotação nº {cotacao.numero}</h1>
        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${cotacao.status === "aberta" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
          {(cotacao.status || "").toUpperCase()}
        </span>
      </div>
      {cotacao.titulo && <p className="text-slate-600 mb-4">{cotacao.titulo}</p>}

      {/* Banner destaque: acesso ao Mapa quando existem propostas recebidas */}
      {(() => {
        const respondidas = cotacao.fornecedores.filter((f) => f.respondida).length;
        if (respondidas === 0) return null;
        return (
          <Card className="mb-4 border-emerald-300 bg-gradient-to-r from-emerald-50 to-blue-50">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <div className="font-semibold text-emerald-900">
                    {respondidas} de {cotacao.fornecedores.length} fornecedor(es) já responderam
                  </div>
                  <div className="text-xs text-emerald-800">Abra o Mapa para comparar propostas e aprovar por item.</div>
                </div>
              </div>
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => nav(`/cotacoes/dashboard/cotacoes/${id}/mapa`)} data-testid="btn-abrir-mapa-banner">
                <Trophy className="w-4 h-4 mr-1" /> Abrir Mapa de Cotação
              </Button>
            </CardContent>
          </Card>
        );
      })()}

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="font-semibold flex items-center gap-2 mb-2"><Package className="w-4 h-4" /> Produtos ({cotacao.produtos.length})</div>
            <ul className="text-sm divide-y max-h-[220px] overflow-y-auto">
              {cotacao.produtos.map((pi) => (
                <li key={pi.produto_id} className="py-1.5 flex justify-between">
                  <span>{pi.produto?.codigo && <span className="text-xs font-mono bg-slate-100 px-1 rounded mr-1">{pi.produto.codigo}</span>}{pi.produto?.nome}</span>
                  <span className="text-slate-500">{pi.qtd} {pi.produto?.unidade || ""}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="font-semibold flex items-center gap-2 mb-2"><Users className="w-4 h-4" /> Fornecedores ({cotacao.fornecedores.length})</div>
            <ul className="text-sm divide-y max-h-[220px] overflow-y-auto">
              {cotacao.fornecedores.map((fi) => (
                <li key={fi.fornecedor_id} className="py-1.5 flex justify-between">
                  <span>{fi.fornecedor?.razao_social}</span>
                  <span className="text-xs">{fi.respondida ? <Check className="w-4 h-4 text-emerald-600 inline" /> : <X className="w-4 h-4 text-slate-400 inline" />}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="mb-3 font-semibold flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-600" /> Links de resposta por fornecedor</div>
      <p className="text-sm text-slate-500 mb-3">Envie cada link para o fornecedor correspondente. Ele abre a Loja Digital exclusiva daquela cotação.</p>

      <div className="space-y-3">
        {(links?.fornecedores || []).map((f) => (
          <Card key={f.token_publico} data-testid={`link-fornecedor-${f.fornecedor_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="font-medium">{f.fornecedor_nome}</div>
                  <div className="text-xs text-slate-500 mb-2">
                    {f.vendedor && <>{f.vendedor} · </>}{f.celular || "sem celular"}
                    {f.respondida && <span className="ml-2 px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[11px] font-semibold">RESPONDIDA</span>}
                  </div>
                  <div className="text-xs font-mono bg-slate-50 border rounded px-2 py-1 truncate">{f.loja_url}</div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(f.loja_url)} data-testid={`btn-copy-${f.fornecedor_id}`}>
                    <Copy className="w-4 h-4 mr-1" /> Copiar link
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(f.loja_url, "_blank")} data-testid={`btn-abrir-${f.fornecedor_id}`}>
                    <ExternalLink className="w-4 h-4 mr-1" /> Abrir
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => window.open(f.whatsapp_url, "_blank")} data-testid={`btn-whatsapp-${f.fornecedor_id}`}>
                    <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
